import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit2, Lock, MoreVertical, 
  MapPin, Users, Calendar, Briefcase, Info, List, History, Settings
} from 'lucide-react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { usePermission } from '@/core/permissions/usePermission';
import { useTeamDetail } from './hooks/useTeamDetail';
import { useTeamMembers } from './hooks/useTeamMembers';
import { AddMemberModal } from './components/AddMemberModal';
import { 
  GeneralInfoCard, ProjectListCard, QuickStatsCard, 
  TopMembersCard, RecentActivityCard 
} from './components/TeamDetailCards';
import { MemberManagementBlock } from './components/MemberManagementBlock';
import { ProjectManagementBlock } from './components/ProjectManagementBlock';
import { cn } from '@/utils/cn';

const TABS = [
  { id: 'info', label: 'Thông tin chung', icon: Info },
  { id: 'members', label: 'Thành viên', icon: Users, count: 12 },
  { id: 'projects', label: 'Dự án phụ trách', icon: Briefcase, count: 5 },
  { id: 'history', label: 'Lịch sử hoạt động', icon: History },
  { id: 'audit', label: 'Nhật ký thay đổi', icon: Settings },
];

export default function TeamsDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canUpdate = can('teams' as any, 'update' as any);
  const { data, isLoading, error, refresh: refreshDetail } = useTeamDetail(id || '');
  const { 
    availableUsers, isLoadingUsers, isMutating, 
    addMember, removeMember, updateRole 
  } = useTeamMembers(id || '');
  
  const [activeTab, setActiveTab] = useState('info');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  if (isLoading) return (
    <PageLayout title="Chi tiết team">
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <p className="sm:text-sm text-xs text-slate-400">Đang tải thông tin đội nhóm...</p>
      </div>
    </PageLayout>
  );

  if (error || !data) return (
    <PageLayout title="Lỗi">
      <div className="p-8 text-center bg-white rounded-md border border-slate-100">
        <p className="text-sm text-red-500">Không tìm thấy thông tin tổ đội.</p>
        <Button onClick={() => navigate('/teams')} className="mt-4">Quay lại danh sách</Button>
      </div>
    </PageLayout>
  );

  return (
    <PageLayout
      title={data.name}
      description={data.description || 'Thông tin chi tiết và quá trình vận hành của đội nhóm'}
      headerActions={
        <div className="flex items-center gap-3">
          {canUpdate && (
            <>
              <Button variant="outline" onClick={() => navigate(`/teams/edit/${id}`)} className="h-9 sm:text-sm text-xs px-4">
                <Edit2 className="w-3.5 h-3.5 mr-2" />
                Chỉnh sửa
              </Button>
              <Button variant="outline" className="h-9 sm:text-sm text-xs px-4">
                <Lock className="w-3.5 h-3.5 mr-2" />
                Khóa team
              </Button>
            </>
          )}
          <Button variant="outline" className="h-9 w-9 p-0">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      <div className="max-w-[1600px] mx-auto space-y-8">
        {/* Header Summary Block */}
        <div className="bg-white rounded-md border border-slate-100 p-6">
          <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
            <div className="flex flex-col gap-4 min-w-[300px]">
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => navigate('/teams')} className="w-8 h-8 p-0 rounded-full">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <h1 className="text-lg font-normal text-slate-900">{data.name}</h1>
                <StatusBadge label="Đang hoạt động" variant="success" className="sm:text-sm text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 sm:text-sm text-xs text-slate-500">
                  <span className="w-4 h-4 flex items-center justify-center bg-slate-50 rounded">
                    <List className="w-3 h-3" />
                  </span>
                  Mã team: <span className="font-normal text-slate-700">{data.code}</span>
                </div>
                <div className="flex items-center gap-2 sm:text-sm text-xs text-slate-500">
                  <span className="w-4 h-4 flex items-center justify-center bg-slate-50 rounded">
                    <Briefcase className="w-3 h-3" />
                  </span>
                  Thi công
                </div>
                <div className="flex items-center gap-2 sm:text-sm text-xs text-slate-500">
                  <span className="w-4 h-4 flex items-center justify-center bg-slate-50 rounded">
                    <Info className="w-3 h-3" />
                  </span>
                  Phòng ban: <span className="font-normal text-slate-700">Thi công</span>
                </div>
                <div className="flex items-center gap-2 sm:text-sm text-xs text-slate-500">
                  <span className="w-4 h-4 flex items-center justify-center bg-slate-50 rounded">
                    <Users className="w-3 h-3" />
                  </span>
                  Trưởng team: 
                  <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded-md">
                    {data.leader?.avatar_url && <img src={data.leader.avatar_url} className="w-4 h-4 rounded-full" alt="" />}
                    <span className="font-normal text-blue-600">{data.leader?.full_name}</span>
                  </div>
                </div>
              </div>
              <p className="sm:text-sm text-xs text-slate-400 leading-relaxed italic">{data.description}</p>
            </div>

            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8 border-l border-slate-50 pl-12">
              {[
                { icon: Users, label: 'Số thành viên', value: `${data.member_count} người` },
                { icon: Calendar, label: 'Ngày thành lập', value: new Date(data.founded_date).toLocaleDateString('vi-VN') },
                { icon: Briefcase, label: 'Loại team', value: data.team_type },
                { icon: MapPin, label: 'Khu vực phụ trách', value: data.zone_name },
                { icon: Briefcase, label: 'Dự án phụ trách', value: `${data.project_count} dự án` },
                { icon: Info, label: 'Trạng thái', value: 'Hoạt động', isStatus: true },
              ].map((item, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 sm:text-sm text-xs font-normal text-slate-400 tracking-widest">
                    <item.icon className="w-3 h-3" />
                    {item.label}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.isStatus && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
                    <span className="sm:text-sm text-xs text-slate-700 font-normal">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-8 border-b border-slate-100">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "pb-3 sm:text-sm text-xs font-normal tracking-wider transition-all relative",
                activeTab === tab.id ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"
              )}
            >
              {tab.label} {tab.count !== undefined && <span className="ml-1 sm:text-sm text-xs opacity-70">({tab.count})</span>}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 animate-in fade-in duration-300" />}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <GeneralInfoCard data={data} />
                <ProjectListCard projects={data.projects} />
              </div>
              <div className="space-y-8">
                <QuickStatsCard data={data} />
                <TopMembersCard members={data.members} />
                <RecentActivityCard activities={data.activities} />
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <MemberManagementBlock 
              members={data.members}
              onAddMember={() => setIsAddModalOpen(true)}
              onRemoveMember={(memberId) => removeMember(memberId, refreshDetail)}
              onUpdateRole={(memberId, roleId) => updateRole(memberId, roleId, refreshDetail)}
              canUpdate={canUpdate}
            />
          )}

          {activeTab === 'projects' && (
            <ProjectManagementBlock projects={data.projects} />
          )}

          {['history', 'audit'].includes(activeTab) && (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-md border border-dashed border-slate-200">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-widest">Tính năng đang phát triển</h3>
              <p className="sm:text-sm text-xs text-slate-400 mt-1 italic">Chi tiết {TABS.find(t => t.id === activeTab)?.label} sẽ sớm được cập nhật.</p>
            </div>
          )}
        </div>
      </div>

      <AddMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        availableUsers={availableUsers}
        isLoading={isLoadingUsers}
        isMutating={isMutating}
        onAdd={(userIds, role) => addMember(userIds, role, () => {
          setIsAddModalOpen(false);
          refreshDetail();
        })}
      />
    </PageLayout>
  );
}
